import React from "react";
import { useLocation } from "react-router-dom";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Buildings, GraduationCap, Heart, MagnifyingGlass, MapPin, Medal, Star, X } from "@phosphor-icons/react";
import {
  addPreferredColleges,
  fetchAllApplicantColleges,
  removePreferredColleges,
  SHORTLIST_LIMIT_REACHED_MESSAGE,
  type ApplicantCollege,
} from "../../../hallticket-lib";
import { useAppLayout } from "../../../layout";

function normalizeValue(value: string): string {
  return value.trim().toLowerCase();
}

type CollegesTab = "shortlisted" | "colleges";

type CollegeTypeTag = {
  label: string;
  className: "iits" | "nits" | "private" | "deemed" | "other";
};

function getCollegeTypeTag(college: ApplicantCollege): CollegeTypeTag | null {
  const type = normalizeValue(college.type ?? "");
  const name = normalizeValue(college.name);

  if (type.includes("iit") || /^iit\b/.test(name)) {
    return { label: "IIT", className: "iits" };
  }
  if (type.includes("nit") || /^nit\b/.test(name)) {
    return { label: "NIT", className: "nits" };
  }
  if (type.includes("deemed") || type.includes("iiit") || /^iiit\b/.test(name)) {
    return { label: "DEEMED", className: "deemed" };
  }
  if (type.includes("private") || type.includes("pvt")) {
    return { label: "PRIVATE", className: "private" };
  }

  const fallbackLabel = (college.type ?? "").trim().toUpperCase();
  if (fallbackLabel) {
    return { label: fallbackLabel, className: "other" };
  }
  return null;
}

function getCollegeLocationText(college: ApplicantCollege): string {
  const details = college.apiDetails ?? [];
  const cityFromDetails = details.find((item) => {
    const key = normalizeValue(item.label);
    return key === "city" || key.endsWith(" city");
  })?.value;
  const stateFromDetails = details.find((item) => {
    const key = normalizeValue(item.label);
    return key === "state" || key === "state code" || key.endsWith(" state");
  })?.value;
  const parts = [
    college.city?.trim() || cityFromDetails?.trim(),
    college.stateCode?.trim() || stateFromDetails?.trim(),
  ].filter(Boolean) as string[];
  return parts.join(", ");
}

function splitCourseText(value: string): string[] {
  return value
    .split(/\s*(?:,|\/|\||;|\u00B7)\s*/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function toCourseShortForm(value: string): string {
  const normalized = value.trim();
  if (!normalized) {
    return "";
  }

  const compact = normalized.replace(/[^a-zA-Z]/g, "");
  const likelyShortForm =
    compact.length > 1
    && compact.length <= 8
    && compact === compact.toUpperCase();
  if (likelyShortForm) {
    return compact;
  }

  const words = normalized
    .replace(/[()]/g, " ")
    .split(/[^a-zA-Z0-9]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (words.length === 0) {
    return normalized.toUpperCase();
  }

  const filteredWords = words.filter(
    (word) => !["and", "of", "in", "the", "for", "with", "to"].includes(word.toLowerCase()),
  );
  const sourceWords = filteredWords.length > 0 ? filteredWords : words;
  const abbreviation = sourceWords
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");

  return abbreviation || normalized.toUpperCase();
}

function toCourseShortForms(values: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const short = toCourseShortForm(value);
    if (!short || seen.has(short)) {
      return;
    }
    seen.add(short);
    result.push(short);
  });
  return result;
}

function getCollegeCoursesText(college: ApplicantCollege): string | null {
  const courseNames = (college.courses ?? [])
    .map((item) => item.trim())
    .filter(Boolean);
  if (courseNames.length > 0) {
    const shortForms = toCourseShortForms(courseNames);
    if (shortForms.length > 0) {
      return `Courses: ${shortForms.join(", ")}`;
    }
  }

  const details = college.apiDetails ?? [];
  const shortCoursesFromDetails = details.find((item) => {
    const key = normalizeValue(item.label);
    return (key.includes("course") || key.includes("branch")) && (key.includes("short") || key.includes("abbr"));
  })?.value;
  if (shortCoursesFromDetails?.trim()) {
    const shortForms = toCourseShortForms(splitCourseText(shortCoursesFromDetails));
    if (shortForms.length > 0) {
      return `Courses: ${shortForms.join(", ")}`;
    }
  }

  const dynamicCourseDetail = details.find((item) => {
    const key = normalizeValue(item.label);
    return (key.includes("course") || key.includes("branch")) && item.value.trim();
  })?.value;
  if (dynamicCourseDetail?.trim()) {
    const shortForms = toCourseShortForms(splitCourseText(dynamicCourseDetail));
    if (shortForms.length > 0) {
      return `Courses: ${shortForms.join(", ")}`;
    }
  }
  return null;
}

function getCollegeRankText(college: ApplicantCollege): string | null {
  if (typeof college.nirfRank === "number" && Number.isFinite(college.nirfRank)) {
    return `#${college.nirfRank}`;
  }

  const details = college.apiDetails ?? [];
  const rankDetail = details.find((item) => normalizeValue(item.label).includes("rank"))?.value;
  if (!rankDetail?.trim()) {
    return null;
  }
  const normalized = rankDetail.trim();
  return normalized.startsWith("#") ? normalized : `#${normalized}`;
}

function getCollegeRankValue(college: ApplicantCollege): number {
  if (typeof college.nirfRank === "number" && Number.isFinite(college.nirfRank)) {
    return college.nirfRank;
  }
  const details = college.apiDetails ?? [];
  const rankDetail = details.find((item) => normalizeValue(item.label).includes("rank"))?.value;
  if (rankDetail?.trim()) {
    const parsed = parseInt(rankDetail.replace(/[^0-9]/g, ""), 10);
    if (!isNaN(parsed)) {
      return parsed;
    }
  }
  return Infinity;
}

function CollegeCardBody({ college }: { college: ApplicantCollege }) {
  const typeTag = getCollegeTypeTag(college);
  const locationText = getCollegeLocationText(college);
  const courseText = getCollegeCoursesText(college);
  const rankText = getCollegeRankText(college);

  return (
    <>
      <div className="cs-card-head">
        <h5>{college.name}</h5>
        {typeTag && <span className={`cs-card-tag ${typeTag.className}`}>{typeTag.label}</span>}
      </div>
      {locationText && (
        <div className="cs-card-line cs-card-location">
          <MapPin size={11} weight="fill" aria-hidden />
          <span>{locationText}</span>
        </div>
      )}
      {courseText && (
        <div className="cs-card-line cs-card-courses">
          <GraduationCap size={11} weight="fill" aria-hidden />
          <span>{courseText}</span>
        </div>
      )}
      {rankText && (
        <div className="cs-card-rank">
          <Medal size={12} weight="fill" aria-hidden />
          <span>
            HallTicket Rank: <strong>{rankText}</strong>
          </span>
        </div>
      )}
    </>
  );
}

export function CollegesDirectory() {
  const { showToast } = useAppLayout();
  const location = useLocation();
  const [activeTab, setActiveTab] = React.useState<CollegesTab>("shortlisted");
  const [collegesStatus, setCollegesStatus] = React.useState<"idle" | "loading" | "success" | "error">("idle");
  const [collegesError, setCollegesError] = React.useState("");
  const [collegesData, setCollegesData] = React.useState<ApplicantCollege[]>([]);
  const [searchText, setSearchText] = React.useState("");

  const runCollegesLookup = React.useCallback(async () => {
    setCollegesStatus("loading");
    setCollegesError("");

    try {
      const data = await fetchAllApplicantColleges();
      setCollegesData(data);
      setCollegesStatus("success");
    } catch (error) {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Failed to fetch colleges list.";
      setCollegesError(message);
      setCollegesData([]);
      setCollegesStatus("error");
    }
  }, []);

  React.useEffect(() => {
    if (collegesStatus === "idle") {
      void runCollegesLookup();
    }
  }, [collegesStatus, runCollegesLookup]);

  React.useEffect(() => {
    const state = location.state as { collegeSearchTab?: string } | null;
    const requestedTab = state?.collegeSearchTab;
    if (requestedTab === "colleges" || requestedTab === "shortlisted") {
      setActiveTab(requestedTab);
      setSearchText("");
      return;
    }
    if (requestedTab === "courses") {
      setActiveTab("colleges");
      setSearchText("");
    }
  }, [location.state]);

  const shortlistedColleges = React.useMemo(
    () => collegesData.filter((college) => college.preferred),
    [collegesData],
  );

  const totalShortlistedCount = shortlistedColleges.length;

  const filteredCollegeCards = React.useMemo(() => {
    const query = normalizeValue(searchText);
    const filtered = collegesData
      .filter((college) => {
        return (
          !query
          || normalizeValue(college.name).includes(query)
          || normalizeValue(college.city ?? "").includes(query)
          || normalizeValue(college.stateCode ?? "").includes(query)
          || normalizeValue(college.type ?? "").includes(query)
          || normalizeValue((college.courses ?? []).join(" ")).includes(query)
          || (typeof college.nirfRank === "number" && normalizeValue(String(college.nirfRank)).includes(query))
          || normalizeValue(String(college.collegeId)).includes(query)
          || (college.homeUniversity ? normalizeValue(college.homeUniversity).includes(query) : false)
        );
      });

    return filtered.sort((a, b) => {
      const rankA = getCollegeRankValue(a);
      const rankB = getCollegeRankValue(b);
      if (rankA !== rankB) {
        return rankA - rankB;
      }
      return a.name.localeCompare(b.name);
    });
  }, [searchText, collegesData]);

  const updatePreferredState = React.useCallback((collegeId: number, preferred: boolean) => {
    setCollegesData((prev) =>
      prev.map((college) =>
        college.collegeId === collegeId ? { ...college, preferred } : college,
      ),
    );
  }, []);

  const handlePreferredToggle = React.useCallback(
    async (rowData: ApplicantCollege) => {
      const nextPreferred = !rowData.preferred;
      const collegeId = rowData.collegeId;

      setCollegesError("");
      updatePreferredState(collegeId, nextPreferred);

      try {
        if (nextPreferred) {
          await addPreferredColleges([collegeId]);
        } else {
          await removePreferredColleges([collegeId]);
        }
      } catch (error) {
        updatePreferredState(collegeId, !nextPreferred);
        const message =
          error instanceof Error && error.message
            ? error.message
            : "Failed to update preferred college.";
        if (nextPreferred && message === SHORTLIST_LIMIT_REACHED_MESSAGE) {
          showToast({
            severity: "warn",
            summary: "Shortlist Limit Reached",
            detail: SHORTLIST_LIMIT_REACHED_MESSAGE,
            life: 5000,
          });
          return;
        }
        setCollegesError(message);
      }
    },
    [showToast, updatePreferredState],
  );

  const showSearch = activeTab === "colleges";
  const searchPlaceholder = "Search by college name or city...";

  const shortlistedEmpty = totalShortlistedCount === 0;
  const shortlistedCollegeCards = filteredCollegeCards.filter((college) => college.preferred);

  return (
    <section className="colleges-directory">
      <div className="cs-tab-row" role="tablist" aria-label="College search views">
        <button
          type="button"
          className={`cs-tab-btn${activeTab === "shortlisted" ? " active" : ""}`}
          onClick={() => {
            setActiveTab("shortlisted");
            setSearchText("");
          }}
        >
          <Star size={12} weight="fill" />
          Shortlisted
          <span className="cs-tab-count">{totalShortlistedCount}</span>
        </button>
        <button
          type="button"
          className={`cs-tab-btn${activeTab === "colleges" ? " active" : ""}`}
          onClick={() => {
            setActiveTab("colleges");
            setSearchText("");
          }}
        >
          <Buildings size={12} weight="fill" />
          Colleges
        </button>
      </div>

      {showSearch && (
        <div className="colleges-search-wrap">
          <MagnifyingGlass size={13} weight="bold" className="colleges-search-icon" />
          <InputText
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={searchPlaceholder}
            className="colleges-search"
          />
          {searchText.trim() && (
            <button
              type="button"
              className="colleges-search-clear"
              onClick={() => setSearchText("")}
              aria-label="Clear search"
              title="Clear search"
            >
              <X size={14} weight="bold" aria-hidden />
            </button>
          )}
        </div>
      )}

      {activeTab === "colleges" && <div className="cs-chip-row" style={{ minHeight: "38px" }} aria-hidden="true" />}

      {collegesError && <p className="rr-error-msg">{collegesError}</p>}

      <div className="colleges-table-wrap">
        {collegesStatus === "loading" && (
          <div className="colleges-loader-overlay" aria-live="polite" aria-busy="true">
            <ProgressSpinner
              style={{ width: "2.4rem", height: "2.4rem" }}
              strokeWidth="6"
              animationDuration=".9s"
            />
            <span className="colleges-loader-text">Fetching colleges...</span>
          </div>
        )}

        {activeTab === "shortlisted" && shortlistedEmpty && (
          <div className="cs-empty-wrap">
            <Star size={44} weight="fill" className="cs-empty-star" />
            <h3>Nothing shortlisted yet</h3>
            <p>Browse colleges and shortlist the ones you are interested in — they&apos;ll all appear here.</p>
            <div className="cs-empty-actions">
              <button type="button" className="cs-btn-primary" onClick={() => setActiveTab("colleges")}>
                <Buildings size={13} weight="fill" /> Search Colleges
              </button>
            </div>
          </div>
        )}

        {activeTab === "shortlisted" && !shortlistedEmpty && (
          <div className="cs-shortlisted-content">
            {shortlistedCollegeCards.length > 0 && (
              <section className="cs-shortlisted-block">
                <div className="cs-shortlisted-header">
                  <h4 className="cs-shortlisted-title"><Buildings size={14} weight="fill" /> COLLEGES</h4>
                </div>
                <div className="cs-card-grid">
                  {shortlistedCollegeCards.map((college) => (
                    <article key={college.collegeId} className="cs-card">
                      <CollegeCardBody college={college} />
                      <button type="button" className="cs-card-btn remove" onClick={() => void handlePreferredToggle(college)}>
                        <X size={12} weight="bold" /> Remove
                      </button>
                    </article>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}

        {activeTab === "colleges" && (
          <div className="cs-card-grid">
            {filteredCollegeCards.map((college) => (
              <article key={college.collegeId} className="cs-card">
                <CollegeCardBody college={college} />
                <button
                  type="button"
                  className={`cs-card-btn${college.preferred ? " active" : ""}`}
                  onClick={() => void handlePreferredToggle(college)}
                >
                  <Heart size={12} weight={college.preferred ? "fill" : "regular"} />
                  {college.preferred ? "Shortlisted" : "Shortlist"}
                </button>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
